import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import {
	AssignmentStatus,
	type IInventoryItem,
	InventoryItemCondition,
	InventoryListingStatus,
	Services,
	type ServicesProviderInventory,
	SyncStatus,
	validateReadyForOnlineSale,
} from '@ekhodealer/ekho-common';
import { Box, ContentWithTooltip, Group, SkeletonRect,Stack, Switch, Text } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { formatTimestampToDate,getDaysElapsed } from '@common/lib/time';

import { useGetInventoryItem } from '../../../api/useGetInventoryItem';
import { usePatchInventoryItem } from '../../../api/usePatchInventoryItem';
import { drawerAtom } from '../../../atoms/editDrawerAtoms';
import { getListingStatusTagProps } from '../../../lib/utils';
import { PublishBlockers } from '../../PublishBlockers';

/**
 * Get tooltip message for disabled online sales switch
 */
const getOnlineSalesDisabledTooltip = (
	item: IInventoryItem | null | undefined,
	servicesProvider: ServicesProviderInventory | undefined,
	isPending: boolean,
): string | null => {
	if (isPending) {
		return 'Updating...';
	}

	if (!item) {
		return 'Item not available';
	}

	if (!servicesProvider) {
		return 'Services provider not available';
	}

	if (!validateReadyForOnlineSale(item, servicesProvider)) {
		const reasons: string[] = [];

		if (item.newUsed !== InventoryItemCondition.NEW) {
			reasons.push('Not a new vehicle');
		}

		if (item.matching?.status !== 'MATCHED') {
			reasons.push('Vehicle not fully matched to a vehicle');
		}

		if (!item.pricing?.retailPrice || item.pricing.retailPrice <= 0) {
			reasons.push('Missing or invalid retail price');
		}

		if (!item.vin || item.vin.length === 0) {
			reasons.push('Missing VIN');
		}

		if (!servicesProvider.config?.[Services.INVENTORY]?.isLiveForOnlineSales) {
			reasons.push('Your dealership is not configured for online sales');
		}

		if (item.assignment?.status !== AssignmentStatus.AVAILABLE) {
			reasons.push('Unable to find this item in the DMS');
		}

		if (reasons.length > 0) {
			return reasons.join(', ');
		}

		return 'Item does not meet requirements for online sales';
	}

	return null;
};

export const InventoryStatus = () => {
	const { selectedItemId } = useAtomValue(drawerAtom);
	console.log('selectedItemId', selectedItemId);
	const { data: item, isLoading } = useGetInventoryItem(selectedItemId || '');
	const patchInventoryItem = usePatchInventoryItem();
	const { data: servicesProvider } = useCurrentServicesProvider() as {
		data: ServicesProviderInventory | undefined;
	};

	// publish status
	const listingStatus = item?.listing?.status;
	const statusProps = listingStatus ? getListingStatusTagProps(listingStatus) : null;
	const publishStatusLabel = statusProps?.children || '';

	const publishedDate = item?.listing?.lastPublished
		? formatTimestampToDate(item.listing.lastPublished)
		: null;

	// this needs to be for early return 
	const publishStatusDescription = useMemo(() => {

		switch (listingStatus) {
			case InventoryListingStatus.PUBLISHED:
				return publishedDate ? `Published on ${publishedDate}` : 'Published';
			case InventoryListingStatus.ARCHIVED:
			case InventoryListingStatus.HIDDEN:
				return 'Archived on ...';
			case InventoryListingStatus.DRAFT:
				return 'Ready to publish';
			case InventoryListingStatus.LOCKED:
			case InventoryListingStatus.INCOMPLETE_DRAFT:
				return 'Vehicle details incomplete';
			default:
				return 'Unknown';
		}
	}, [listingStatus, publishedDate]);

	// online sales switch disabled state and tooltip
	const isSwitchDisabled =
		!item ||
		!servicesProvider ||
		!validateReadyForOnlineSale(item, servicesProvider) ||
		patchInventoryItem.isPending;

	const disabledTooltip = useMemo(
		() => getOnlineSalesDisabledTooltip(item, servicesProvider, patchInventoryItem.isPending),
		[item, servicesProvider, patchInventoryItem.isPending],
	);

	// Show loading if actively loading OR if we have an ID but no data yet
	if (isLoading || Boolean(selectedItemId && !item)) {
		return (
			<AccordionSection title='Inventory Status' defaultExpanded>
				<div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
					<Stack itemsSpacing='16'>
						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
							<Box rounding='12' background='secondary' padding='16'>
								<Stack itemsSpacing='8'>
									<SkeletonRect width='80px' height='14px' />
									<SkeletonRect width='100px' height='20px' />
									<SkeletonRect width='120px' height='14px' />
								</Stack>
							</Box>
							<Box rounding='12' background='secondary' padding='16'>
								<Stack itemsSpacing='8'>
									<SkeletonRect width='90px' height='14px' />
									<SkeletonRect width='110px' height='20px' />
									<SkeletonRect width='140px' height='14px' />
								</Stack>
							</Box>
						</div>
						<Box rounding='12' background='secondary' padding='16'>
							<Group itemsSpacing='12' itemsAlignY='center' noWrap>
								<div style={{ flex: 1, minWidth: 0 }}>
									<Stack itemsSpacing='8'>
										<SkeletonRect width='80px' height='14px' />
										<div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
											<SkeletonRect width='70px' height='16px' />
											<SkeletonRect width='180px' height='14px' />
										</div>
									</Stack>
								</div>
								<SkeletonRect width='44px' height='24px' rounding='12' />
							</Group>
						</Box>
					</Stack>
				</div>
			</AccordionSection>
		);
	}

	if (!item) {
		return null;
	}
	// Online sales
	const isOnlineSalesEnabled = item.isMarkedForOnlineSale || false;
	const onlineSalesLabel = isOnlineSalesEnabled ? 'Enabled' : 'Disabled';
	const onlineSalesDescription = isOnlineSalesEnabled ? 'Customers can purchase this vehicle online' : 'Inquiry only (lead generation)';

	const handleOnlineSalesToggle = async (isSelected: boolean) => {
		if (!item.id) return;
		
		try {
			await patchInventoryItem.mutateAsync({
				id: item.id,
				updates: {
					isMarkedForOnlineSale: isSelected,
				},
			});
		} catch (error) {
			console.error('Failed to update online sales:', error);
		}
	};

	// sync status
	const syncStatus = item.syncStatus;
	const syncStatusLabel = syncStatus === SyncStatus.SYNCED ? 'Complete' : 
		syncStatus === SyncStatus.IN_PROGRESS ? 'In progress' :
		syncStatus === SyncStatus.NEEDS_REVIEW ? 'Needs review' :
		syncStatus === SyncStatus.FIX_REQUIRED ? 'Fix required' : 'Unknown';
		
	const lastSyncedDays = item.syncStatusResolvedAt 
		? getDaysElapsed(item.syncStatusResolvedAt)
		: null;
	const lastSyncedText = lastSyncedDays !== null 
		? `Last synced: ${lastSyncedDays} ${lastSyncedDays === 1 ? 'day' : 'days'} ago`
		: 'Last synced: Unknown';

	

	return (
		<AccordionSection title='Inventory Status' defaultExpanded>
			<div style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
				<Stack itemsSpacing='16'>
					<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
					<Box rounding='12' background='secondary' padding='12'>
						<Stack itemsSpacing='8'>
							<Text size='footnote' color='secondary'>
								Sync status
							</Text>
							<Text size='body' weight='semibold'>
								{syncStatusLabel}
							</Text>
							<Text size='bodySmall' color='secondary'>
								{lastSyncedText}
							</Text>
						</Stack>
					</Box>
					<Box rounding='12' background='secondary' padding='12'>
						<Stack itemsSpacing='8'>
							<Text size='footnote' color='secondary'>
								Publish status
							</Text>
							<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
								<Text size='body' weight='semibold'>
									{publishStatusLabel}
								</Text>
								{(listingStatus === InventoryListingStatus.INCOMPLETE_DRAFT || listingStatus === InventoryListingStatus.LOCKED) && statusProps?.icon && item?.id && (
									<ContentWithTooltip
										tooltipContent={<PublishBlockers inventoryItemId={item.id} />}
										content={
											<div style={{ color: '#D97F18', display: 'flex', alignItems: 'center' }}>
												{statusProps.icon}
											</div>
										}
									/>
								)}
							</div>
							<Text size='bodySmall' color='secondary'>
								{publishStatusDescription}
							</Text>
						</Stack>
					</Box>
				</div>
				<Box rounding='12' background='secondary' padding='12'>
					<Group itemsSpacing='12' itemsAlignY='center' noWrap>
						<div style={{ flex: 1, minWidth: 0 }}>
							<Stack itemsSpacing='8'>
								<Text size='footnote' color='secondary'>
									Online sales
								</Text>
								<div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', wordBreak: 'break-word' }}>
									<Text size='body' weight='semibold'>
										{onlineSalesLabel}
									</Text>
									<Text size='bodySmall' color='secondary'>
										{onlineSalesDescription}
									</Text>
								</div>
							</Stack>
						</div>
						{isSwitchDisabled && disabledTooltip ? (
							<ContentWithTooltip
								tooltipContent={disabledTooltip}
								content={
									<Switch
										isSelected={isOnlineSalesEnabled}
										onChange={handleOnlineSalesToggle}
										isDisabled={isSwitchDisabled}
									/>
								}
							/>
						) : (
							<Switch
								isSelected={isOnlineSalesEnabled}
								onChange={handleOnlineSalesToggle}
								isDisabled={isSwitchDisabled}
							/>
						)}
					</Group>
				</Box>
				</Stack>
			</div>
		</AccordionSection>
	);
};
